/**
 * Integration of camera settings with the property system
 *
 * This component:
 * - Listens for camera rotation changes from the CameraAlignmentTool
 * - Persists rotation values to the database per camera setting type
 * - Restores saved rotations when the project loads
 */

import { useEffect, useContext, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { RendererContext } from '../../../contexts/RendererContext';
import { useProjectPropertyManager, PropertyType, PropertyValueType } from '../index';

/**
 * Camera setting type identifiers (mirrors CameraSettingType from potree_fork)
 * These define the different camera configurations that can have their own rotation
 */
enum CameraSettingType {
  ORTHOGRAPHIC_TOP = 'orthographic_top',
  ORTHOGRAPHIC_PLANE = 'orthographic_plane',
  ORTHOGRAPHIC_SIDE = 'orthographic_side',
  PERSPECTIVE_3D = 'perspective_3d',
}

/** Event type for rotation changes (mirrors CameraSettingsEvents from potree_fork) */
const ROTATION_CHANGED_EVENT = 'camera_rotation_changed';

/** Event data interface for rotation changed event */
interface CameraRotationChangedEvent {
  type: string;
  cameraSettingType: CameraSettingType;
  /** Screen heading in radians (mirrors CameraSettings.heading in potree_fork). */
  heading: number;
  source: string;
}

// Property keys for camera settings
export enum CameraSettingsPropertyKeys {
  ROTATION_ORTHOGRAPHIC_TOP = 'camera_rotation_orthographic_top',
  ROTATION_ORTHOGRAPHIC_PLANE = 'camera_rotation_orthographic_plane',
  ROTATION_ORTHOGRAPHIC_SIDE = 'camera_rotation_orthographic_side',
  ROTATION_PERSPECTIVE_3D = 'camera_rotation_perspective_3d',
}

// Map CameraSettingType to property keys
const cameraSettingTypeToPropertyKey: Record<CameraSettingType, CameraSettingsPropertyKeys> = {
  [CameraSettingType.ORTHOGRAPHIC_TOP]: CameraSettingsPropertyKeys.ROTATION_ORTHOGRAPHIC_TOP,
  [CameraSettingType.ORTHOGRAPHIC_PLANE]: CameraSettingsPropertyKeys.ROTATION_ORTHOGRAPHIC_PLANE,
  [CameraSettingType.ORTHOGRAPHIC_SIDE]: CameraSettingsPropertyKeys.ROTATION_ORTHOGRAPHIC_SIDE,
  [CameraSettingType.PERSPECTIVE_3D]: CameraSettingsPropertyKeys.ROTATION_PERSPECTIVE_3D,
};

// Property identifier used for all camera settings (project-level, not per-object)
const CAMERA_SETTINGS_IDENTIFIER = 'camera_settings';

interface CameraSettingsPropertyIntegrationProps {
  /** Called when camera settings are loaded from database */
  onSettingsLoaded?: () => void;
}

/**
 * Hook to integrate camera settings with the property system
 * Should be used in a component that has access to the viewer
 */
export function useCameraSettingsPropertyIntegration(props?: CameraSettingsPropertyIntegrationProps) {
  const { projectId = '' } = useParams();
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const settingsLoadedRef = useRef(false);

  const { getPropertyValue, updateProperty } = useProjectPropertyManager();

  // Save rotation to database
  const saveRotation = useCallback(
    async (cameraSettingType: CameraSettingType, roll: number) => {
      const propertyKey = cameraSettingTypeToPropertyKey[cameraSettingType];
      if (!propertyKey) return;

      try {
        await updateProperty({
          identifier: propertyKey,
          value: roll,
          valueType: PropertyValueType.FLOAT,
          propertyType: PropertyType.PROJECT_USER,
          propertyIdentifier: CAMERA_SETTINGS_IDENTIFIER,
        });
      } catch (error) {
        console.error('[CameraSettingsPropertyIntegration] Error saving rotation:', error);
      }
    },
    [updateProperty],
  );

  // Load all saved rotations from database and apply to CameraSettingsManager
  const loadSavedRotations = useCallback(() => {
    if (!viewer?.cameraSettingsManager || settingsLoadedRef.current) return;

    const cameraSettingTypes = Object.values(CameraSettingType) as CameraSettingType[];

    for (const cameraSettingType of cameraSettingTypes) {
      const propertyKey = cameraSettingTypeToPropertyKey[cameraSettingType];
      if (!propertyKey) continue;

      const savedRoll = getPropertyValue(CAMERA_SETTINGS_IDENTIFIER, propertyKey, PropertyType.PROJECT_USER) as number | undefined;

      if (savedRoll !== undefined && savedRoll !== null) {
        // Stored radians are re-interpreted as the class's canonical heading
        // (same DB keys, no migration — the value was always consumed as
        // roll by every restore path).
        viewer.cameraSettingsManager.loadSettings(cameraSettingType, { heading: savedRoll });
      }
    }

    settingsLoadedRef.current = true;
    props?.onSettingsLoaded?.();
  }, [viewer, getPropertyValue, props]);

  // Listen for rotation changes from CameraSettingsManager
  useEffect(() => {
    if (!viewer?.cameraSettingsManager) return;

    const handleRotationChanged = (event: { type: string; [key: string]: unknown }) => {
      const rotationEvent = event as unknown as CameraRotationChangedEvent;
      saveRotation(rotationEvent.cameraSettingType, rotationEvent.heading);
    };

    viewer.cameraSettingsManager.addEventListener(ROTATION_CHANGED_EVENT, handleRotationChanged);

    return () => {
      viewer.cameraSettingsManager.removeEventListener(ROTATION_CHANGED_EVENT, handleRotationChanged);
    };
  }, [viewer, saveRotation]);

  // Load saved rotations when viewer becomes available
  useEffect(() => {
    if (viewer?.cameraSettingsManager && projectId) {
      // Small delay to ensure properties are loaded
      const timeoutId = setTimeout(loadSavedRotations, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [viewer, projectId, loadSavedRotations]);

  return {
    saveRotation,
    loadSavedRotations,
  };
}

/**
 * Component that integrates camera settings with the property system
 * Mount this component in your project view to enable camera rotation persistence
 */
export function CameraSettingsPropertyIntegration(props: CameraSettingsPropertyIntegrationProps) {
  useCameraSettingsPropertyIntegration(props);
  return null; // This component doesn't render anything
}

export default CameraSettingsPropertyIntegration;
